import { CalendarIcon, Pencil2Icon } from '@radix-ui/react-icons'
import { useEffect, useRef, useState } from 'react'
import {
	Link,
	useActionData,
	useFetcher,
	useLocation,
	useNavigate,
} from 'react-router'
import {
	arePricesPending,
	eventPrice,
	type EventPrices,
	type FestivalPricingInput,
	isPayWhatYouCan,
	pricesOf,
	TICKET_TYPE_LABEL,
	TICKET_TYPES,
	type TicketType,
} from '#app/domains/pricing/pure'
import { eventEnd, formatClock, isEventPassed } from '#app/domains/program/pure'
import {
	getMapsDirectionsUrl,
	isSoldOut,
	seatsRemaining,
	seatsTaken,
} from '#app/domains/tickets/pure'
import { formatNOK } from '#app/utils/formatNOK.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { Odometer } from '../../atoms/Odometer.tsx'
import { QuantityStepper } from '../../molecules/QuantityStepper.tsx'
import { ShareLinks } from '../../molecules/ShareLinks.tsx'
import { SeatMeter } from '../../molecules/ticket/SeatMeter.tsx'

type PricedEvent = Partial<EventPrices> & {
	id: string
	title?: string
	date: Date | string
	endsAt?: Date | string | null
	dropIn?: boolean | null
	location?: string | null
	host?: string | null
	seats?: { total: number } | null
	tickets?: ReadonlyArray<{ paymentStatus?: string | null }> | null
	ticketCount?: number | null
	festival?: FestivalPricingInput | null
}

const TYPE_ROWS: Array<{ type: TicketType; label: string; hint?: string }> =
	TICKET_TYPES.map((type) => ({
		type,
		label: TICKET_TYPE_LABEL[type],
		hint: type === 'child' ? '12 år og under' : undefined,
	}))

/** What the concert's action answers a press with. */
type Answer = {
	error?: string
	/** tickets put in the cart, and how many of each type */
	added?: number
	lines?: Array<{ type: TicketType; count: number }>
}

const priceLabel = (price: number | null) =>
	price == null ? null : price === 0 ? 'Gratis' : formatNOK(price)

/**
 * Back from logging in, which the buyer did after pressing "Legg i korga":
 * finish that press, once. The link back is replaced by the bare concert
 * page first, so neither a reload nor Back can add the tickets again; then
 * the picks go to the action like a press, whose checks decide and whose
 * error shows in the card. Leaving the page meanwhile calls it off. Without
 * JavaScript nothing happens here, and the buyer presses once more.
 * True while it is under way.
 */
function useAddOnArrival(
	enabled: boolean,
	quantities: Partial<Record<TicketType, number>> | undefined,
	submit: (
		target: Record<string, string>,
		options: { method: 'post' },
	) => Promise<void>,
) {
	const navigate = useNavigate()
	const location = useLocation()
	const [adding, setAdding] = useState(false)
	const started = useRef(false)
	// the picks, and the page they wait on until its link back is replaced
	const pending = useRef<{
		pathname: string
		picks: Record<string, string>
	} | null>(null)

	useEffect(() => {
		if (!enabled || started.current) return
		started.current = true
		pending.current = {
			pathname: location.pathname,
			picks: Object.fromEntries(
				Object.entries(quantities ?? {}).map(([type, count]) => [
					`${type}Qty`,
					String(count),
				]),
			),
		}
		setAdding(true)
		void navigate(
			{ pathname: location.pathname, hash: location.hash },
			{ replace: true, preventScrollReset: true },
		)
	}, [enabled, quantities, location, navigate])

	useEffect(() => {
		const waiting = pending.current
		if (!waiting) return
		const samePage = location.pathname === waiting.pathname
		if (samePage && location.search) return // not replaced yet
		pending.current = null
		if (!samePage) {
			setAdding(false)
			return
		}
		void submit({ ...waiting.picks, stay: '1' }, { method: 'post' }).finally(
			() => setAdding(false),
		)
	}, [location, submit])

	return adding
}

/**
 * The ticket card on the concert page: seats left, then a quantity stepper
 * per ticket type with the concert's own prices and a live total, and the
 * way to Kassen. Plain number inputs underneath, so it works without
 * JavaScript; the steppers and the rolling total are the enhancement.
 * A drop-in event has nothing to buy: the card says to just come, when.
 */
export function DetailCtaCard({
	event,
	isIOS,
	initialQuantities,
	addOnArrival = false,
	inCart = 0,
	reserved = 0,
	vippsOnline = true,
	calendarUrl,
	shareUrl,
}: {
	event: PricedEvent
	isIOS: boolean
	/** pre-filled counts, e.g. after logging in on the way to the cart */
	initialQuantities?: Partial<Record<TicketType, number>>
	/**
	 * back from logging in, which the buyer did after pressing "Legg i korga":
	 * add the pre-filled tickets by themselves, once, as that press would have
	 */
	addOnArrival?: boolean
	/** this buyer's tickets for this concert in the cart, not yet settled in Kassen */
	inCart?: number
	/** this buyer's tickets for this concert held for the door, or paid */
	reserved?: number
	/** Vipps payment on the site is live; until then everyone pays at the door */
	vippsOnline?: boolean
	/** an .ics for this concert, when available */
	calendarUrl?: string
	/** the concert page's absolute URL, to share it */
	shareUrl?: string
}) {
	const user = useOptionalUser()
	// adding stays on the page (the URL too): a fetcher posts the picks, marked
	// to stay once the page runs; without JavaScript the form posts as usual
	// and goes on to Kassen
	const fetcher = useFetcher<Answer>()
	const [scripted, setScripted] = useState(false)
	useEffect(() => setScripted(true), [])
	const posted = useActionData() as Answer | undefined
	const actionData = fetcher.data ?? posted
	const canEdit = user?.roles.some((role) =>
		role.permissions.some(
			(p) =>
				p.action === 'update' && p.entity === 'event' && p.access === 'any',
		),
	)
	const passed = isEventPassed(eventEnd(event))
	const soldOut = isSoldOut(event)
	const remaining = seatsRemaining(event)
	const eventPrices = pricesOf(event)
	// the picker is for PER_CONCERT festivals only (see arePricesPending):
	// every type it offers has its own price
	const rows = TYPE_ROWS.filter(
		(row) => eventPrice(eventPrices, row.type) != null,
	)
	const tba = arePricesPending(event)

	const [counts, setCounts] = useState<Record<TicketType, number>>(
		() =>
			Object.fromEntries(
				TICKET_TYPES.map((type) => [type, initialQuantities?.[type] ?? 0]),
			) as Record<TicketType, number>,
	)
	const picked = rows.reduce((sum, row) => sum + counts[row.type], 0)
	const total = rows.reduce(
		(sum, row) =>
			sum + counts[row.type] * (eventPrice(eventPrices, row.type) ?? 0),
		0,
	)
	const full = remaining != null && picked >= remaining
	const set = (type: TicketType, value: number) =>
		setCounts((current) => ({
			...current,
			[type]: Math.max(0, Math.min(value, remaining ?? 99)),
		}))
	// only while the picker is on offer: otherwise the card already says why
	// nothing can be bought
	const adding = useAddOnArrival(
		addOnArrival && !event.dropIn && !passed && !soldOut && !tba,
		initialQuantities,
		fetcher.submit,
	)
	const busy = fetcher.state !== 'idle' || adding
	// once tickets are in the cart the card lists them instead of the picker,
	// so it never looks as if nothing was added; "Vel fleire billettar …"
	// brings the picker back, empty, for more of this concert
	const [reopened, setReopened] = useState<Answer | undefined>(undefined)
	const added = actionData?.added && reopened !== actionData ? actionData : null
	const pickMore = () => {
		setReopened(actionData)
		setCounts(
			Object.fromEntries(TICKET_TYPES.map((type) => [type, 0])) as Record<
				TicketType,
				number
			>,
		)
	}
	// the press's button goes with the picker: keep the keyboard on the card
	const addedRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (added) addedRef.current?.focus()
	}, [added])

	const editLink = canEdit ? (
		<Link to={`/events/${event.id}/edit`} className="tp-edit">
			<Pencil2Icon aria-hidden="true" /> Rediger
		</Link>
	) : null
	const links = (
		<div className="tp-links">
			{calendarUrl && !passed ? (
				<a href={calendarUrl} className="tp-link">
					<CalendarIcon aria-hidden="true" /> Legg i kalender
				</a>
			) : null}
			{event.location && /\d/.test(event.location) ? (
				<a
					href={getMapsDirectionsUrl(event.location, isIOS ? 'ios' : 'android')}
					target="_blank"
					rel="noopener noreferrer"
					className="tp-link"
				>
					Få vegbeskriving
				</a>
			) : null}
			{shareUrl ? (
				<ShareLinks
					url={shareUrl}
					title={event.title ?? 'Huskonsertfestivalen'}
					linkClassName="tp-link"
				/>
			) : null}
		</div>
	)

	if (event.dropIn) {
		return (
			<aside id="billetter" className="tp" aria-labelledby="tp-title">
				<div className="tp-head">
					<h2 id="tp-title" className="tp-title">
						Berre møt opp
					</h2>
					{editLink}
				</div>
				{passed ? (
					<p className="tp-state">
						<span className="tp-stamp">Fullført</span>
						Dette arrangementet er over — takk til alle som kom!
					</p>
				) : (
					<p className="tp-state tp-state-soon">
						<span className="tp-soon">Treng ikkje billett</span>
						{event.endsAt
							? `Kom innom når det passar, mellom ${formatClock(event.date)} og ${formatClock(event.endsAt)}.`
							: `Kom når det passar, frå kl. ${formatClock(event.date)}.`}
					</p>
				)}
				{links}
			</aside>
		)
	}

	return (
		<aside id="billetter" className="tp" aria-labelledby="tp-title">
			<div className="tp-head">
				<h2 id="tp-title" className="tp-title">
					Billettar
				</h2>
				{editLink}
			</div>

			{remaining != null && event.seats?.total ? (
				<div className="tp-seats">
					<SeatMeter total={event.seats.total} taken={seatsTaken(event)} />
					<span className="sr-only">Ledige plassar: {remaining}</span>
				</div>
			) : null}

			{passed ? (
				<p className="tp-state">
					<span className="tp-stamp">Fullført</span>
					Denne konserten er over — takk til alle som kom!
				</p>
			) : soldOut ? (
				<p className="tp-state">
					<span className="tp-stamp">Utseld</span>
					Alle plassane er tekne. Følg med — det kan bli ledige plassar igjen.
				</p>
			) : tba ? (
				<p className="tp-state tp-state-soon">
					<span className="tp-soon">Billettar kjem</span>
					Prisane blir lagde ut snart.
				</p>
			) : (
				<fetcher.Form method="post" className="tp-form">
					{scripted ? <input type="hidden" name="stay" value="1" /> : null}
					{added ? (
						<div
							ref={addedRef}
							tabIndex={-1}
							className="tp-added"
							role="status"
						>
							<p className="tp-added-title">
								{added.added === 1
									? '1 billett er lagd i korga'
									: `${added.added} billettar er lagde i korga`}
							</p>
							<ul className="tp-added-lines">
								{(added.lines ?? []).map((line) => {
									const price = eventPrice(eventPrices, line.type)
									return (
										<li key={line.type}>
											<span>
												{line.count} ×{' '}
												{TYPE_ROWS.find((row) => row.type === line.type)
													?.label ?? line.type}
											</span>
											<span>
												{priceLabel(price == null ? null : price * line.count)}
											</span>
										</li>
									)
								})}
							</ul>
							<div className="tp-added-actions">
								<Link to="/tickets/checkout" className="tp-added-go">
									Til kassa
								</Link>
								<Link to="/#program" className="tp-added-more">
									Legg til fleire konsertar
								</Link>
							</div>
							<button
								type="button"
								className="tp-added-again"
								onClick={pickMore}
							>
								Vel fleire billettar til denne konserten
							</button>
						</div>
					) : (
						<>
							{isPayWhatYouCan(eventPrices) ? (
								<p className="tp-note tp-evne">
									Pris etter evne: vel sjølv den prisen som passar for deg.
								</p>
							) : null}
							<div className="tp-rows">
								{rows.map((row) => {
									const label = priceLabel(eventPrice(eventPrices, row.type))
									const inputId = `tp-${row.type}`
									return (
										<div key={row.type} className="tp-row">
											<label htmlFor={inputId} className="tp-type">
												<span className="tp-type-name">{row.label}</span>
												<span className="tp-type-meta">
													{[label, row.hint].filter(Boolean).join(' · ')}
												</span>
											</label>
											<QuantityStepper
												id={inputId}
												name={`${row.type}Qty`}
												value={counts[row.type]}
												onChange={(value) => set(row.type, value)}
												noun={row.label.toLowerCase()}
												max={remaining ?? undefined}
												full={full}
											/>
										</div>
									)
								})}
							</div>

							<p className="tp-total" aria-live="polite">
								<span>Totalt</span>
								<span className="tp-total-sum">
									<Odometer value={total} minDigits={1} />
									<span aria-hidden="true"> kr</span>
									<span className="sr-only">{formatNOK(total)}</span>
								</span>
							</p>

							{actionData?.error ? (
								<p className="tp-error" role="alert">
									{actionData.error}
								</p>
							) : null}

							<button
								type="submit"
								className="tp-submit"
								disabled={picked === 0 || busy}
							>
								<span>Legg i korga</span>
								{picked > 0 ? (
									<span className="tp-submit-count">
										{picked} {picked === 1 ? 'billett' : 'billettar'}
									</span>
								) : null}
							</button>
							<p className="tp-note">
								{vippsOnline
									? 'I kassa vel du å betale med Vipps no eller i døra.'
									: 'Du betaler i døra. Snart kan du òg betale med Vipps her på nettsida.'}
								{user ? null : ' Du loggar inn med e-posten din på vegen.'}
							</p>
						</>
					)}
					{inCart > 0 && !added ? (
						<Link to="/tickets/checkout" className="tp-cart">
							Du har {inCart} {inCart === 1 ? 'billett' : 'billettar'} til denne
							konserten i korga — gå til kassa
						</Link>
					) : null}
					{reserved > 0 && user ? (
						<Link
							to={`/users/${user.username}/tickets`}
							className="tp-cart tp-cart-mine"
						>
							Du har {reserved} {reserved === 1 ? 'billett' : 'billettar'} til
							denne konserten — sjå billettane dine
						</Link>
					) : null}
				</fetcher.Form>
			)}

			{links}
		</aside>
	)
}
